import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFavoris } from '../context/FavorisContext';
import { useTheme } from '../context/ThemeContext';
import { getImageUrl } from '../utils/imageUtils';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { totalFavoris } = useFavoris();
  const { darkMode, toggleDarkMode } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRefDesktop = useRef(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/produits?recherche=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // Fermer le menu utilisateur si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      const desktopRef = userMenuRefDesktop.current;
      
      if (isUserMenuOpen && desktopRef && !desktopRef.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Fonction pour obtenir les initiales de l'utilisateur
  const getUserInitials = () => {
    if (user?.nom) {
      return user.nom
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'U';
  };

  // Fonction pour obtenir l'URL de la photo de profil
  const getProfileImageUrl = () => {
    if (user?.photoProfil) {
      if (user.photoProfil.startsWith('http')) return user.photoProfil;
      return getImageUrl(user.photoProfil);
    }
    return null;
  };

  return (
    <nav className="bg-gradient-to-r from-white to-yellow-50 dark:from-gray-800 dark:to-gray-900 shadow-md sticky top-0 z-50 border-b-2 border-primary-800 dark:border-primary-600 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4">
        {/* Version Mobile - Barre de recherche + Logo compact */}
        <div className="md:hidden flex items-center gap-2 py-2">
          <Link to="/" className="flex-shrink-0 hover:scale-105 transition-transform">
            <span className="font-display text-lg font-bold text-primary-800 dark:text-primary-400 leading-none">Village Artisanal</span>
          </Link>
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pr-10 rounded-full border-2 border-primary-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none text-base bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                style={{ fontSize: '16px' }}
              />
              <button 
                type="submit" 
                className="absolute right-2 w-7 h-7 flex items-center justify-center bg-primary-800 dark:bg-primary-600 text-white rounded-full hover:bg-primary-900 dark:hover:bg-primary-700 transition-colors focus:outline-none shadow-sm"
                aria-label="Rechercher"
              >
                <i className="fas fa-search text-xs"></i>
              </button>
            </div>
          </form>
          {/* Bouton Mode Sombre Mobile */}
          <button
            onClick={toggleDarkMode}
            className="flex-shrink-0 p-2 text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all"
            aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
          >
            {darkMode ? (
              <i className="fas fa-sun text-lg"></i>
            ) : (
              <i className="fas fa-moon text-lg"></i>
            )}
          </button>
        </div>

        {/* Version Desktop - Layout original */}
        <div className="hidden md:flex justify-between items-center h-20">
          <Link to="/" className="flex flex-col items-start hover:scale-105 transition-transform">
            <span className="font-display text-xl font-bold text-primary-800 dark:text-primary-400 leading-none">Village Artisanal</span>
            <span className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Sénégal</span>
          </Link>

            <div className="flex flex-row items-center space-x-8" role="menu" aria-label="Main navigation">
            {/* Navigation Desktop (sans icônes) */}
            <Link to="/" role="menuitem" className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300">
              Accueil
            </Link>
            <Link to="/produits" role="menuitem" className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300">
              Produits
            </Link>
            <Link to="/artisans" role="menuitem" className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300">
              Artisans
            </Link>
            <Link to="/a-propos" role="menuitem" className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300">
              À Propos
            </Link>
            <Link to="/contact" role="menuitem" className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300">
              Contact
            </Link>

            {/* Mes Produits - Desktop (si authentifié et pas admin) */}
            {isAuthenticated && user?.role !== 'admin' && (
              <Link 
                to="/mes-produits" 
                role="menuitem" 
                className="flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 font-medium px-4 py-2 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300" 
              >
                <i className="fas fa-box text-xl"></i>
                <span>Mes Produits</span>
              </Link>
            )}

            {/* Favoris Desktop (seulement si connecté) */}
            {isAuthenticated && user && (
            <Link 
              to="/favoris" 
              role="menuitem" 
                className="flex relative items-center gap-2 text-primary-900 dark:text-gray-100 hover:text-red-500 dark:hover:text-red-400 font-medium px-4 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300" 
              aria-label={`Favoris (${totalFavoris})`}
            >
              <i className="fas fa-heart text-xl"></i>
              {totalFavoris > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {totalFavoris}
                </span>
              )}
            </Link>
            )}

            {/* Bouton Mode Sombre Desktop */}
            <button
              onClick={toggleDarkMode}
              className="text-primary-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-accent-300"
              aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {darkMode ? (
                <i className="fas fa-sun text-xl"></i>
              ) : (
                <i className="fas fa-moon text-xl"></i>
              )}
            </button>

            <div className="flex flex-row items-center space-x-4 md:ml-8">
              {isAuthenticated ? (
                <>
                  {/* Menu utilisateur Desktop */}
                  <div className="relative" ref={userMenuRefDesktop}>
                    <button
                      onClick={toggleUserMenu}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gradient-to-r from-primary-50 to-accent-50 dark:from-gray-700 dark:to-gray-800 border-2 border-primary-200 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400 shadow-sm hover:shadow-md"
                      aria-label="Menu utilisateur"
                      aria-expanded={isUserMenuOpen}
                    >
                      {/* Avatar */}
                      <div className="relative">
                        {getProfileImageUrl() ? (
                          <img
                            src={getProfileImageUrl()}
                            alt={user?.nom || 'Utilisateur'}
                            className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-600 shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold text-sm border-2 border-white dark:border-gray-600 shadow-sm ${getProfileImageUrl() ? 'hidden' : 'flex'}`}
                        >
                          {getUserInitials()}
                        </div>
                        {/* Badge en ligne */}
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-600 rounded-full"></div>
                      </div>
                      
                      {/* Nom et rôle */}
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-semibold text-primary-900 dark:text-gray-100">
                          {user?.nom || 'Utilisateur'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {user?.role === 'admin' ? 'Administrateur' : 'Artisan'}
                        </span>
                      </div>
                      
                      {/* Icône chevron */}
                      <i className={`fas fa-chevron-${isUserMenuOpen ? 'up' : 'down'} text-xs text-gray-500 dark:text-gray-400 transition-transform duration-200`}></i>
                    </button>

                    {/* Dropdown Menu Desktop */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border-2 border-primary-200 dark:border-gray-700 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* En-tête du menu */}
                      <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-gray-700 dark:to-gray-800 border-b border-primary-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          {getProfileImageUrl() ? (
                            <img
                              src={getProfileImageUrl()}
                              alt={user?.nom || 'Utilisateur'}
                              className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-gray-600 shadow-md"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-bold border-2 border-white dark:border-gray-600 shadow-md ${getProfileImageUrl() ? 'hidden' : 'flex'}`}
                          >
                            {getUserInitials()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-primary-900 dark:text-gray-100 truncate">
                              {user?.nom || 'Utilisateur'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user?.email || ''}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Options du menu */}
                      <div className="py-2">
                        <Link
                          to="/profile"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-gray-700 transition-colors group"
                        >
                          <i className="fas fa-user-circle w-5 text-primary-600 dark:text-primary-400 group-hover:scale-110 transition-transform"></i>
                          <span>Mon Profil</span>
                  </Link>

                        {user?.role === 'admin' && (
                          <Link
                            to="/admin"
                            onClick={() => {
                              setIsUserMenuOpen(false);
                            }}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors group"
                          >
                            <i className="fas fa-shield-alt w-5 group-hover:scale-110 transition-transform"></i>
                            <span>Administration</span>
                    </Link>
                        )}

                        <Link
                          to="/favoris"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-gray-700 transition-colors group"
                        >
                          <i className="fas fa-heart w-5 text-red-500 group-hover:scale-110 transition-transform"></i>
                          <span>Mes Favoris</span>
                          {totalFavoris > 0 && (
                            <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                              {totalFavoris}
                            </span>
                          )}
                    </Link>

                        <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors group"
                        >
                          <i className="fas fa-sign-out-alt w-5 group-hover:scale-110 transition-transform"></i>
                          <span>Déconnexion</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </>
              ) : (
                <div className="flex flex-row space-x-2">
                  <Link to="/login" className="text-primary-800 border border-primary-800 px-4 py-2 rounded-lg hover:bg-primary-800 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent-300">
                    Connexion
                  </Link>
                  <Link to="/register" className="bg-accent-500 text-white px-4 py-2 rounded-lg hover:bg-accent-600 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-300">
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;